// src/pages/transaction-confirmation.page.tsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Form, Button, Col, Row, Spinner } from "react-bootstrap";
import TransactionSummary from "../components/transaction-summary.component";
import { useAuth } from "../components/autentication-context.component";
import { ConfirmTransaction } from "../interfaces/confirm-transaction.interface";
import { TransactionStatus } from "../../enum/transaction-status.enum";
import { findTermsAndConditionsByEmployee } from "../../services/terms-and-conditions.service";
import { requestEarlyWage } from "../../services/account.service";
import { handleApiError } from "../../services/error.service";
import { LogoutProps } from "../interfaces/logout.interface";

const TransactionConfirmation: React.FC<LogoutProps> = ({ handleLogout }) => {
  const { employeeId, token, updateToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const transactionDetails = location.state as ConfirmTransaction;

  const [loading, setLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [termsUrl, setTermsUrl] = useState<string | null>(null);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);

  useEffect(() => {
    if (!employeeId || !token) return;

    const fetchTermsAndConditions = async () => {
      try {
        setLoading(true);
        const data = await findTermsAndConditionsByEmployee(employeeId, token, updateToken);
        setTermsUrl(data?.url || null);
      } catch (error) {
        handleApiError({ error, handleLogout, setNotification });
      } finally {
        setLoading(false);
      }
    };

    fetchTermsAndConditions();
  }, [employeeId, token]);

  if (!transactionDetails) {
    return <h4>No transaction details available.</h4>;
  }

  const navigateToTransactionDetails = (status: TransactionStatus, message: string) => {
    navigate("/transaction-details", {
      state: {
        status,
        message,
        transaction: "N/A",
        detail: {
          requestedAmount: transactionDetails.requestedAmount,
          date: new Date().toLocaleString(),
        },
        destinationAccount: {
          bankAccountName: "N/A",
          bankAccountNumber: "N/A",
        },
      },
    });
  };

  const confirmTransaction = async () => {
    try {
      setLoading(true);
      const { requestedAmount, accountId } = transactionDetails;
      const response = await requestEarlyWage(requestedAmount, accountId, employeeId!, token!, updateToken);

      if (!response?.ok) {
        navigateToTransactionDetails(TransactionStatus.Failed, response?.message || "Unknown error occurred.");
        return;
      }

      navigate("/transaction-details", { state: response });
    } catch (error) {
      navigateToTransactionDetails(TransactionStatus.Failed, "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      {/* Transaction Summary */}
      <Row className="mb-4">
        <Col>
          <TransactionSummary transaction={transactionDetails} />
        </Col>
      </Row>

      {/* Terms and Conditions (Only Show if Not Loading) */}
      {!loading && (
        <Row className="mb-4">
          <Col>
            {termsUrl ? (
              <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="d-flex text-decoration-none">
                Read Terms and Conditions
              </a>
            ) : notification?.type === "error" ? (
              <p className="text-danger">{notification.message}</p>
            ) : (
              <p>Loading terms and conditions...</p>
            )}
          </Col>
        </Row>
      )}

      {/* Checkbox */}
      {!loading && termsUrl && (
        <Row className="mb-4">
          <Col>
            <Form.Group controlId="termsCheckbox" className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                label="I accept the terms and conditions"
                checked={isTermsAccepted}
                onChange={(e) => setIsTermsAccepted(e.target.checked)}
                className="me-2"
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {/* Confirm Button */}
      <Row>
        <Col>
          <Button
            className="greenButton w-100 btn-lg"
            onClick={confirmTransaction}
            disabled={loading || !isTermsAccepted}
          >
            {loading ? <Spinner as="span" animation="border" size="sm" /> : "Confirm Transaction"}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default TransactionConfirmation;